import type { 
  ClientId, 
  IntegrityReportId, 
  InspectionId,
  ProfessionalId
} from "../../shared/kernel/identifiers.js";
import type { EngineeringAssessment } from "./integrity-assessment.js";
import type { GeneratedLineList } from "../inspection-book/line-list.js";

export type IntegrityReportStatus = 'DRAFT' | 'IN_ELABORATION' | 'FINALIZED_BY_INSPECTOR' | 'SUBMITTED_TO_ENGINEERING' | 'UNDER_ENGINEERING_REVIEW' | 'RETURNED' | 'IN_CORRECTION' | 'APPROVED' | 'AWAITING_SIGNATURE' | 'SIGNED' | 'COMPLETED';

export type DamageMechanism = {
  mechanismId: string;
  name: string;
  confirmed: boolean;
};

export interface IntegrityReport {
  id: IntegrityReportId;
  reportNumber?: string | null;
  clientReportNumber?: string | null;
  clientId: ClientId;
  inspectionId: InspectionId;
  equipmentSnapshotId: string | null;

  status: IntegrityReportStatus;

  objective: string | null;
  referenceDocuments: string | null;
  inspectionObservations: string | null;
  maintenanceServices: string | null;
  nextInspectionDates: string | null;
  damageMechanisms: string | null;
  conclusion: string | null;
  restrictions?: string | null;
  inspectorId: ProfessionalId | null;
  engineerPlhId: ProfessionalId | null;

  sentToPlhAt: string | null;
  sentToPlhBy: string | null;
  returnedBy: string | null;
  returnedAt: string | null;
  returnComment: string | null;
  approvedByPlh: string | null;
  approvedAt: string | null;
  cancelledBy: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;

  // "Validar Relatório" (-> APPROVED) tracking (Bloco 9) — distinct from
  // approvedByPlh/approvedAt above, which record the final PLH signature
  // (AWAITING_SIGNATURE). Validating is Engineering declaring the report
  // technically conforme; it is not the final signature.
  validatedAt?: string | null;
  validatedByUserId?: string | null;
  validatedByProfessionalId?: string | null;

  verificationToken?: string | null;
  verificationCode?: string | null;
  documentHash?: string | null;
  revision?: string | null;

  /**
   * Avaliação de Engenharia (Painel de Engenharia): CONDIÇÃO DO EQUIPAMENTO
   * (APTO / NÃO APTO / A CRITÉRIO DO CLIENTE) com resultado derivado e VIDA
   * RESIDUAL em meses (AVR ou informada pelo Engenheiro, limitada a 240).
   */
  engineeringAssessment?: EngineeringAssessment | null;

  /**
   * Engenheiro/PLH do Projeto congelados no envio ao PLH (Painel de
   * Engenharia). Mudanças futuras no Projeto não alteram a revisão enviada.
   */
  responsibility?: {
    projectId: string; projectCode: string; projectName: string; clientId: string;
    engineerProfessionalId: string | null; engineerName: string; engineerFunctionTitle: string; engineerCredential: string;
    plhProfessionalId: string | null; plhName: string; plhFunctionTitle: string; plhCredential: string; frozenAt: string;
  } | null;

  /** FINALIZAR RELATÓRIO (≠ assinar): snapshot final, edição ordinária bloqueada. */
  finalization?: {
    finalizedAt: string; finalizedByUserId: string; finalizedByName: string; revision: string; snapshotId: string | null;
    state: "FINALIZADO" | "FINALIZADO_AGUARDANDO_ASSINATURA";
  } | null;

  /**
   * LINHAS QUE COMPOE O SISTEMA — congelado na finalização (comando
   * complementar, item 12): `null` para RI de qualquer contexto que não seja
   * Sistema de Tubulação. Enquanto o RI está em rascunho, este campo fica
   * vazio e a composição é lida ao vivo do cadastro (ReportService).
   */
  lineListSnapshot?: GeneratedLineList | null;

  /**
   * CRIADOR DO RELATÓRIO (usuário autenticado que solicitou a criação do
   * rascunho inicial) — congelado na criação, NUNCA alterado pela reatribuição
   * de Inspetor Responsável (Fase 8: Criador ≠ Responsável).
   */
  createdBy?: string | undefined;

  version: number;
  createdAt: string;
  updatedAt: string;
}

export function validateIntegrityReportSignatories(report: IntegrityReport): void {
  if (report.status === 'AWAITING_SIGNATURE' || report.status === 'SIGNED' || report.status === 'COMPLETED') {
    if (!report.inspectorId || !report.engineerPlhId) {
      throw new Error('Inspector and Engineer PLH are required signatories for the Integrity Report');
    }
  }
}

/**
 * Regra canônica de autorização para exclusão de Relatório de Integridade.
 *
 * Distingue:
 * 1. EXCLUSÃO OPERACIONAL:
 *    - Status DRAFT ou IN_ELABORATION
 *    - Sem finalização gravada nem envio ao PLH
 *    - Requer permissão ReportUpdate e titularidade do inspetor quando aplicável
 *
 * 2. EXCLUSÃO ADMINISTRATIVA:
 *    - Requer permissão ReportDelete (ex.: Administrador)
 *    - Permite DRAFT, IN_ELABORATION, FINALIZED_BY_INSPECTOR, SUBMITTED_TO_ENGINEERING,
 *      UNDER_ENGINEERING_REVIEW, RETURNED, IN_CORRECTION, APPROVED, AWAITING_SIGNATURE (sem assinaturas)
 *
 * 3. HARD DELETE PROIBIDO:
 *    - SIGNED ou COMPLETED (documentos imutáveis)
 */
export function canDeleteReport(
  report: IntegrityReport | null | undefined,
  context?: { permissions?: ReadonlySet<string> | Set<string> | readonly string[]; professionalId?: string | null | undefined } | null
): boolean {
  if (!report) return false;

  // SIGNED e COMPLETED são imutáveis — Hard Delete permanentemente proibido
  if (report.status === 'SIGNED' || report.status === 'COMPLETED') {
    return false;
  }

  const permissions = context?.permissions
    ? (context.permissions instanceof Set || ('has' in context.permissions && typeof (context.permissions as any).has === 'function')
        ? (context.permissions as ReadonlySet<string>)
        : new Set(context.permissions as readonly string[]))
    : null;

  const hasReportDelete = permissions ? permissions.has('report.delete') : false;

  // 1. Exclusão Administrativa (Admin com ReportDelete)
  if (hasReportDelete) {
    return true;
  }

  // 2. Exclusão Operacional (Inspetor/Responsável com ReportUpdate)
  const isDraftOrElaboration = report.status === 'DRAFT' || report.status === 'IN_ELABORATION';
  if (!isDraftOrElaboration) return false;
  if (report.finalization) return false;
  if (report.documentHash) return false;
  if (report.approvedByPlh || report.approvedAt) return false;
  if (report.sentToPlhAt) return false;

  // Se context fornecido, valida titularidade do inspetor
  if (permissions && !permissions.has('report.read_all') && context?.professionalId) {
    if (report.inspectorId && String(report.inspectorId) !== String(context.professionalId)) {
      return false;
    }
  }

  return true;
}
